/**
 * Shapes the Integrations page hands to its Active and Directory views.
 *
 * Both views are derived entirely from data the page already loads — MSP-level
 * `Platform` rows and per-customer `Application` rows — so the Directory/Active
 * split needed no schema change. The only new idea is `ConnectionScope`, which
 * reads the existing distinction (a platform serves the whole MSP, an
 * application is connected inside one customer) rather than storing it.
 */
import { Application, Customer, Platform } from "../../types/integration";
import { IntegrationCategoryId } from "../../utils/connectorUtils";

export type ConnectionScope = "msp" | "customer";

/** One live connection, flattened so Active can list platforms and apps together. */
export interface ActiveConnection {
  /** Stable within a render; `${scope}:${id}` plus customer for per-customer rows. */
  key: string;
  name: string;
  description: string;
  logo: string;
  category: IntegrationCategoryId;
  scope: ConnectionScope;
  enabled: boolean;
  /** Absent for MSP-level connections. */
  customer?: Customer;
  platform?: Platform & { enabled?: boolean };
  application?: Application;
  /** Matches the page's `togglingKey` so a row can show its own saving state. */
  togglingKey: string;
}

/** A catalogue row in Directory, with whether it is already connected. */
export interface DirectoryEntry {
  key: string;
  name: string;
  description: string;
  logo: string;
  category: IntegrationCategoryId;
  scope: ConnectionScope;
  /** How many live connections exist for this entry (customers, for per-customer apps). */
  connectedCount: number;
  /** Roadmap entry: listed in the Directory but not connectable yet. */
  comingSoon?: boolean;
}

/** Groups Active rows under the MSP, then one group per customer. */
export interface ActiveGroup {
  id: string;
  title: string;
  subtitle: string;
  scope: ConnectionScope;
  connections: ActiveConnection[];
}

/**
 * MSP-wide connections first, then a group per customer that has any.
 * Customers with nothing connected are omitted rather than shown empty.
 */
export function groupConnectionsByCustomer(connections: ActiveConnection[]): ActiveGroup[] {
  const mspLevel = connections.filter((c) => c.scope === "msp");
  const groups: ActiveGroup[] = [];

  if (mspLevel.length > 0) {
    groups.push({
      id: "msp",
      title: "MSP-wide",
      subtitle: "One connection serves every customer",
      scope: "msp",
      connections: mspLevel,
    });
  }

  const byCustomer = new Map<string, ActiveGroup>();
  connections
    .filter((c) => c.scope === "customer" && c.customer)
    .forEach((connection) => {
      const customer = connection.customer!;
      const id = String(customer.id);
      if (!byCustomer.has(id)) {
        byCustomer.set(id, {
          id,
          title: customer.name,
          subtitle: customer.is_msp_org ? "Your organization" : "Customer tenant",
          scope: "customer",
          connections: [],
        });
      }
      byCustomer.get(id)!.connections.push(connection);
    });

  return [
    ...groups,
    ...Array.from(byCustomer.values()).sort((a, b) => a.title.localeCompare(b.title)),
  ];
}
